/**
 * @fileoverview Syndicator Showdown — Selection Page
 * @module pages/SelectionPage
 *
 * Top-level route component for the player's stable selection flow.
 * Route: /slate/:slateId  (or /game/:slateId for operator-branded URLs)
 *
 * ─── RESPONSIBILITIES ─────────────────────────────────────────────────────────
 *
 * 1. Extract slateId from route params
 * 2. Load slate + races from Firestore (one-time read)
 * 3. Subscribe to real-time race status updates (for live Saturday locking)
 * 4. Handle all access states:
 *      loading       → spinner
 *      error         → error screen with retry
 *      not_found     → "This game doesn't exist"
 *      prices_locked_entries_closed → "Game opens soon" countdown
 *      prices_not_locked → "Fields and prices not yet set" (too early)
 *      returning_player → Load existing entry into EntryContext
 *      open          → Show SelectionPanel (main flow)
 * 5. Wire ReviewSlip open/close state
 * 6. After submission: navigate to My Stable
 *
 * ─── REAL-TIME SUBSCRIPTION STRATEGY ─────────────────────────────────────────
 *
 * Initial load: fetchSlateWithRaces (includes all runner data — heavy, one-time)
 * Live updates: subscribeToRaceStatuses (race status only — lightweight, continuous)
 *
 * Runners don't change after Thursday lock so no runner subscription is needed.
 * Race status changes (locked, resulted) propagate via the race subscription.
 */

import React, {
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef,
  } from 'react';
  
  import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
  import { onAuthStateChanged }                      from 'firebase/auth';
  import { getFunctions, httpsCallable }             from 'firebase/functions';
  import { onSnapshot, doc, updateDoc }               from 'firebase/firestore';
  import { auth, db } from '../services/firebase';
  
  import {
    fetchSlateWithRaces,
    fetchPlayerEntry,
    subscribeToRaceStatuses,
  } from '../services/firestore';
  
  import { SelectionPanel }    from '../components/SelectionPanel';
  import { ReviewSlip }        from '../components/ReviewSlip';
  import { SellEquityModal }   from '../components/SellEquityModal';
  import { EntryProvider }     from '../context/EntryContext';
  import './SelectionPage.css';
  
  // ─── Access State Enum ────────────────────────────────────────────────────────
  
  const ACCESS = {
    LOADING:           'loading',
    ERROR:             'error',
    NOT_FOUND:         'not_found',
    PRICES_NOT_LOCKED: 'prices_not_locked',
    ENTRIES_CLOSED:    'entries_closed',
    FINALISED:         'finalised',
    OPEN:              'open',
  };

  // ─── Timestamp helper ─────────────────────────────────────────────────────────
  // Firestore Timestamps cannot be passed to new Date() — it returns Invalid Date.

  function toMs(val) {
    if (!val) return null;
    if (typeof val === 'number') return val;
    if (typeof val.toMillis === 'function') return val.toMillis();
    if (typeof val.toDate   === 'function') return val.toDate().getTime();
    if (val._seconds !== undefined) return val._seconds * 1000;
    const ms = new Date(val).getTime();
    return isNaN(ms) ? null : ms;
  }
  
  // ─── Loading Screen ───────────────────────────────────────────────────────────
  
  const LoadingScreen = () => (
    <div className="ss-page-center">
      <div className="ss-page-spinner" />
      <p className="ss-page-loading-text">Loading race card…</p>
    </div>
  );
  
  // ─── Back navigation (added to all dead-end screens) ─────────────────────────

  const BackNav = () => (
    <div className="ss-page-nav">
      <a href="/dashboard" className="ss-page-nav-btn ss-page-nav-btn--primary">
        ← Back to Dashboard
      </a>
      <a href="/" className="ss-page-nav-btn ss-page-nav-btn--outline">
        Home
      </a>
    </div>
  );

  // ─── Error Screen ─────────────────────────────────────────────────────────────
  
  const ErrorScreen = ({ message, onRetry }) => (
    <div className="ss-page-center">
      <div className="ss-page-error-icon">⚠</div>
      <h2 className="ss-page-error-title">Something went wrong</h2>
      <p className="ss-page-error-msg">{message}</p>
      {onRetry && (
        <button className="ss-page-retry-btn" onClick={onRetry}>
          Try Again
        </button>
      )}
      <BackNav />
    </div>
  );
  
  // ─── Entries Closed Screen ────────────────────────────────────────────────────

  function fmtSlateTime(val) {
    const ms = toMs(val);
    if (!ms) return null;
    return new Intl.DateTimeFormat('en-AU', {
      weekday: 'long', hour: 'numeric', minute: '2-digit', hour12: true,
    }).format(new Date(ms));
  }
  
  const EntriesClosedScreen = ({ slate }) => {
    const openAt     = fmtSlateTime(slate?.entriesOpenAt || slate?.gameOpenTime);
    const lockTimeAt = fmtSlateTime(slate?.priceLockTime);
  
    return (
      <div className="ss-page-center">
        <div className="ss-page-closed-icon">🏇</div>
        <h2 className="ss-page-closed-title">
          {slate?.pricesLocked
            ? 'Entries Open Soon'
            : 'Fields Not Yet Set'}
        </h2>
        <p className="ss-page-closed-desc">
          {slate?.pricesLocked
            ? openAt
              ? `Selections open ${openAt}.`
              : 'Selections will open once the operator confirms the game.'
            : lockTimeAt
              ? `Share prices are being set ${lockTimeAt}. Check back then.`
              : 'Share prices will be set before this game opens. Check back soon.'}
        </p>
        {slate?.gameName && (
          <p className="ss-page-closed-slate">{slate.gameName}</p>
        )}
        <BackNav />
      </div>
    );
  };

  // ─── Finalised/Completed Screen ───────────────────────────────────────────────
  // A completed showdown's /slate/{slateId} link (e.g. an old notification CTA,
  // bookmark, or "Board" button) must never show the "Entries Open Soon" copy —
  // that's actively misleading once the game is over. Point players straight at
  // the leaderboard instead.

  const FinalisedScreen = ({ slate, slateId }) => (
    <div className="ss-page-center">
      <div className="ss-page-closed-icon">🏁</div>
      <h2 className="ss-page-closed-title">This Showdown is complete</h2>
      <p className="ss-page-closed-desc">
        View the leaderboard and your race results.
      </p>
      {slate?.gameName && (
        <p className="ss-page-closed-slate">{slate.gameName}</p>
      )}
      <div className="ss-page-nav">
        <a href={`/leaderboard/${slateId}`} className="ss-page-nav-btn ss-page-nav-btn--primary">
          View Results
        </a>
        <a href="/showdowns" className="ss-page-nav-btn ss-page-nav-btn--outline">
          Showdowns
        </a>
      </div>
    </div>
  );

  // ─── Custom Hook: Slate Data ──────────────────────────────────────────────────
  
  /**
   * Manages slate data loading and real-time race status updates.
   * Separating data concerns from presentation keeps SelectionPage clean.
   */
  function useSlateData(slateId) {
    const [slate,     setSlate]     = useState(null);
    const [races,     setRaces]     = useState([]);
    const [access,    setAccess]    = useState(ACCESS.LOADING);
    const [error,     setError]     = useState(null);
    const [loadCount, setLoadCount] = useState(0); // Increments to trigger retry
    const [perRaceTradeWindow, setPerRaceTradeWindow] = useState(false);
  
    useEffect(() => {
      if (!slateId) {
        setAccess(ACCESS.NOT_FOUND);
        return;
      }
  
      setAccess(ACCESS.LOADING);
      setError(null);
  
      let raceStatusUnsub = null;
  
      fetchSlateWithRaces(slateId)
        .then(({ slate: loadedSlate, races: loadedRaces }) => {
          setSlate(loadedSlate);
          setRaces(loadedRaces);
  
          // Determine access state from timestamps (not the stored `status` field,
          // which can be stale) plus the admin-controlled gates (pricesLocked, entriesOpen).
          const now       = Date.now();
          const openMs    = toMs(loadedSlate.gameOpenTime);
          const closeMs   = toMs(loadedSlate.gameCloseTime);
          const isTimeOpen   = openMs   && now >= openMs;
          const isTimeClosed = closeMs  && now >= closeMs;

          // Fallback: also accept stale status field as an override for admin-set states
          const statusOverrideOpen = loadedSlate.status === 'live' ||
                                     loadedSlate.status === 'entries_open';

          const isPerRaceMode = loadedSlate.entryCloseMode === 'per_race';

          if (loadedSlate.finalised) {
            // Game fully settled — send everyone to the leaderboard, never the
            // entry flow (which would otherwise show a misleading "Entries
            // Open Soon" via ENTRIES_CLOSED — see FinalisedScreen).
            setPerRaceTradeWindow(false);
            setAccess(ACCESS.FINALISED);
          } else if (!loadedSlate.pricesLocked) {
            // Prices not set yet — too early for anyone
            setPerRaceTradeWindow(false);
            setAccess(ACCESS.PRICES_NOT_LOCKED);
          } else if (isTimeClosed && isPerRaceMode) {
            // gameCloseTime passed AND per_race mode:
            // New players are blocked. Existing submitted players can still trade.
            // Set the trade window flag — SelectionPage resolves the final access
            // state once it knows whether this player has an existing entry.
            setPerRaceTradeWindow(true);
            setAccess(ACCESS.ENTRIES_CLOSED); // default — overridden in SelectionPage if entry exists
          } else if (isTimeClosed) {
            // first_race mode or no entryCloseMode — hard close, no trading
            setPerRaceTradeWindow(false);
            setAccess(ACCESS.ENTRIES_CLOSED);
          } else if (isTimeOpen || statusOverrideOpen || loadedSlate.entriesOpen) {
            // Game is open for new entries
            setPerRaceTradeWindow(false);
            setAccess(ACCESS.OPEN);
          } else {
            // Prices locked but not open yet
            setPerRaceTradeWindow(false);
            setAccess(ACCESS.ENTRIES_CLOSED);
          }
  
          // Subscribe to live race status updates (for Saturday locking)
          // Only merge status fields — don't overwrite runners loaded above
          raceStatusUnsub = subscribeToRaceStatuses(slateId, (updatedRaces) => {
            setRaces((prevRaces) =>
              prevRaces.map((prevRace) => {
                const updated = updatedRaces.find((r) => r.id === prevRace.id);
                if (!updated) return prevRace;
                // Merge only the status fields — preserve runner data from initial load
                return {
                  ...prevRace,
                  status:           updated.status,
                  lockedAt:         updated.lockedAt,
                  result:           updated.result,
                  activeRunnerCount: updated.activeRunnerCount,
                  isDeadHeat:       updated.isDeadHeat,
                  hashManifestId:   updated.hashManifestId,
                };
              })
            );
  
            // If slate just opened (entriesOpen flipped) refresh the access state
            setSlate((prev) => {
              if (!prev) return prev;
              return prev; // Slate-level updates handled by subscribeToSlate if needed
            });
          });
        })
        .catch((err) => {
          const isNotFound = err.message?.includes('not found');
          setAccess(isNotFound ? ACCESS.NOT_FOUND : ACCESS.ERROR);
          setError(err.message);
        });
  
      return () => {
        raceStatusUnsub?.();
      };
    }, [slateId, loadCount]);
  
    const retry = useCallback(() => setLoadCount((c) => c + 1), []);
  
    return { slate, races, access, error, retry, setSlate, setRaces, perRaceTradeWindow };
  }
  
  // ─── Custom Hook: Existing Entry ──────────────────────────────────────────────
  
  /**
   * Loads a player's existing entry if they're returning to edit.
   * Returns null if no prior entry exists.
   */
  function useExistingEntry(slateId, userId, access) {
    const [existingEntry, setExistingEntry] = useState(undefined); // undefined = loading
    const [entryLoading,  setEntryLoading]  = useState(true);
  
    useEffect(() => {
      if (!slateId || !userId) {
        setExistingEntry(null);
        setEntryLoading(false);
        return;
      }

      // Skip the Firestore read when the game is clearly not open yet.
      // ACCESS.PRICES_NOT_LOCKED means no entry document exists — avoid
      // triggering a permission-denied evaluation on a nonexistent resource.
      if (access === ACCESS.PRICES_NOT_LOCKED) {
        setExistingEntry(null);
        setEntryLoading(false);
        return;
      }
  
      fetchPlayerEntry(slateId, userId)
        .then((entry) => {
          setExistingEntry(entry);
          setEntryLoading(false);
        })
        .catch(() => {
          setExistingEntry(null);
          setEntryLoading(false);
        });
    }, [slateId, userId, access]);
  
    return { existingEntry, entryLoading };
  }
  
  // ─── Main SelectionPage ───────────────────────────────────────────────────────
  
  export const SelectionPage = () => {
    const { slateId }  = useParams();
    const navigate     = useNavigate();
    const [searchParams] = useSearchParams();
  
    // ── Auth ──────────────────────────────────────────────────────────────────
    const [currentUser,  setCurrentUser]  = useState(undefined); // undefined = auth loading
    const [authLoading,  setAuthLoading]  = useState(true);
  
    useEffect(() => {
      const unsub = onAuthStateChanged(auth, (user) => {
        setCurrentUser(user);
        setAuthLoading(false);
      });
      return unsub;
    }, []);
  
    // ── Slate data ────────────────────────────────────────────────────────────
    const { slate, races, access, error, retry, perRaceTradeWindow } = useSlateData(slateId);

    // ── Sell equity modal ─────────────────────────────────────────────────────
    // sellModalRunner: { runnerName, currentEquity, currentCost, raceId } | null
    const [sellModalRunner, setSellModalRunner] = useState(null);

    // ── Deep-link reallocation from email buttons ─────────────────────────────
    // ?realloc=<raceId>&action=redistribute|random|manual
    // 'redistribute' and 'random' auto-call the Cloud Function on mount.
    // 'manual' just shows a banner pointing the player to the right race.
    const reallocRaceId = searchParams.get('realloc');
    const reallocAction = searchParams.get('action');
    const targetRaceId  = searchParams.get('race');
    const reallocApplied = useRef(false);

    const [reallocBanner, setReallocBanner] = useState(null);

    useEffect(() => {
      if (reallocApplied.current)               return; // run once only
      if (!reallocRaceId || !reallocAction)     return;
      if (!currentUser)                         return;
      if (resolvedAccess !== ACCESS.OPEN)       return;

      reallocApplied.current = true;

      if (reallocAction === 'manual') {
        setReallocBanner({
          status: 'manual',
          note:   `Your refund is in your wallet. Find the race below to spend it.`,
        });
        return;
      }

      if (reallocAction !== 'redistribute' && reallocAction !== 'random') return;

      setReallocBanner({ status: 'processing' });

      const fns = getFunctions(undefined, 'australia-southeast1');
      const fn  = httpsCallable(fns, 'playerReallocateChoice');
      fn({ slateId, raceId: reallocRaceId, action: reallocAction })
        .then(res => {
          setReallocBanner({
            status: 'success',
            note:   res.data?.allocationNote || 'Your refund has been allocated.',
          });
        })
        .catch(err => {
          setReallocBanner({
            status: 'error',
            note:   err.message || 'Failed to apply action. Please try again from My Stable.',
          });
        });
    }, [reallocRaceId, reallocAction, currentUser, access, slateId]);
  
    // ── Existing entry (returning player) ─────────────────────────────────────
    const userId = currentUser?.uid || null;
    const { existingEntry, entryLoading } = useExistingEntry(slateId, userId, access);

    // ── Real-time scratch alert listener ──────────────────────────────────────
    // Replaces a one-time read so the UI updates the moment the Cloud Function
    // credits the refund and adds a pending_choice alert.
    const [liveScratchAlerts,   setLiveScratchAlerts]   = useState([]);
    const [liveRemainingWallet, setLiveRemainingWallet] = useState(null);

    useEffect(() => {
      if (!userId || !slateId) return;
      // Only subscribe once the game is open — the entry doc doesn't exist before then.
      if (access !== ACCESS.OPEN) return;
      const entryDocId = `${slateId}_${userId}`;
      const unsub = onSnapshot(
        doc(db, 'player_entries', entryDocId),
        (snap) => {
          if (snap.exists()) {
            const data = snap.data();
            setLiveScratchAlerts(data.scratchingAlerts || []);
            setLiveRemainingWallet(data.remainingBankroll ?? null);
          }
        },
        (err) => console.warn('[SelectionPage] entry snapshot error:', err.message),
      );
      return () => unsub();
    }, [userId, slateId, access]);
  
    // ── In-game reallocation handler ──────────────────────────────────────────
    // Called by ScratchAlertBanner buttons inside SelectionPanel.
    // onSnapshot keeps the UI current after the Cloud Function writes.
    const [reallocResult, setReallocResult] = useState(null);
    const [reallocError,  setReallocError]  = useState(null);

    // Marks a pending_choice alert as resolved + dismissed after player acts.
    // Uses the live liveScratchAlerts snapshot so we always write the latest array.
    const resolveAlert = useCallback(async (raceId) => {
      if (!userId || !slateId) return;
      const entryDocId = `${slateId}_${userId}`;
      const resolved = liveScratchAlerts.map(a =>
        a.raceId === raceId && a.allocationStatus === 'pending_choice'
          ? { ...a, allocationStatus: 'resolved', dismissed: true }
          : a
      );
      try {
        await updateDoc(doc(db, 'player_entries', entryDocId), {
          scratchingAlerts: resolved,
        });
      } catch (err) {
        console.warn('[resolveAlert] write failed:', err.message);
      }
    }, [userId, slateId, liveScratchAlerts]);

    const handleRealloc = useCallback(async (alert, action) => {
      setReallocResult(null);
      setReallocError(null);
      try {
        const fns = getFunctions(undefined, 'australia-southeast1');
        const fn  = httpsCallable(fns, 'playerReallocateChoice');
        const res = await fn({
          slateId: alert.slateId || slateId,
          raceId:  alert.raceId,
          action,
        });
        setReallocResult(res.data?.allocationNote || 'Your refund has been allocated.');
        // Mark the alert resolved so the banner disappears and the badge clears
        await resolveAlert(alert.raceId);
      } catch (err) {
        console.error('[handleRealloc]', err);
        setReallocError(err.message || 'Something went wrong. Please try again.');
      }
    }, [slateId, resolveAlert]);

    // ── ReviewSlip state ──────────────────────────────────────────────────────
    const [reviewOpen, setReviewOpen] = useState(false);

    const handleOpenReview  = useCallback(() => setReviewOpen(true),  []);
    const handleCloseReview = useCallback(() => setReviewOpen(false), []);

    // Tapping a race inside the Stable Slip closes it and scrolls the player
    // to that race's card. `nonce` always changes (even for the same raceId
    // tapped twice in a row) so SelectionPanel's effect reliably re-fires.
    const [scrollRequest, setScrollRequest] = useState(null);
    const handleNavigateRace = useCallback((raceId) => {
      setScrollRequest({ raceId, nonce: Date.now() });
    }, []);

    const handleSubmitted = useCallback((entryId) => {
      // Delay navigation to allow ReviewSlip success animation to complete
      setTimeout(() => {
        navigate('/my-stable', { state: { entryId, slateId } });
      }, 2000);
    }, [navigate, slateId]);

    // ── Loading states ────────────────────────────────────────────────────────
    // Resolve final access state for per-race trade window.
    // If gameCloseTime has passed but entryCloseMode is 'per_race',
    // existing submitted players can still edit races that haven't jumped.
    // New players (no existing entry) remain blocked.
    const resolvedAccess = (perRaceTradeWindow && existingEntry?.status === 'submitted')
      ? ACCESS.OPEN
      : access;

    const isLoading = authLoading || access === ACCESS.LOADING || entryLoading;

    // Build initial entry data for EntryContext (if returning player)
    const initialEntryData = useMemo(() => {
      if (!existingEntry) return null;
      // Convert race_allocations from the firestore fetch shape to EntryContext shape
      if (existingEntry.allocations) {
        return { allocations: existingEntry.allocations };
      }
      return null;
    }, [existingEntry]);

    // ── EntryProvider props — hoisted above early returns (Rules of Hooks) ────
    // These are only consumed when access === OPEN, but the useMemo calls must
    // run unconditionally on every render.
    const slateMetadata = useMemo(() => {
      // Return safe defaults if slate hasn't loaded yet
      if (!slate) {
        return {
          slateId,
          totalRaces:      1,
          perRaceFloor:    null,
          entryLockMode:   'per_race',
          avgSpendPerRace: 100000,
        };
      }
      return {
        slateId,
        totalRaces:      slate.totalRaces || slate.selectedRaces?.length || races.length || 1,
        perRaceFloor:    slate.perRaceFloor    ?? null,
        entryLockMode:   slate.entryLockMode   || 'per_race',
        avgSpendPerRace: slate.avgSpendPerRace || 100000,
      };
    }, [slateId, slate, races.length]);

    const racesMetadata = useMemo(() => {
      // Guard: races must be a non-empty array
      if (!Array.isArray(races) || races.length === 0) return {};
      return Object.fromEntries(
        races.map(r => {
          // Safely count active runners — handle null/missing status fields
          const runners = Array.isArray(r.runners) ? r.runners : [];
          const activeRunnerCount = runners.filter(x => {
            if (x.scratched) return false;
            // Use optional chain on BOTH toLowerCase and includes to avoid
            // calling .includes() on undefined when status is null
            const statusLower = x.status?.toLowerCase?.();
            if (statusLower?.includes('scratch')) return false;
            return true;
          }).length || r.activeRunnerCount || 12;

          return [r.id, { activeRunnerCount }];
        })
      );
    }, [races]);
  
    // ── Render ────────────────────────────────────────────────────────────────
  
    if (isLoading) {
      return (
        <div className="dashboard-container">
          <div className="ss-selection-page">
            <LoadingScreen />
          </div>
        </div>
      );
    }
  
    if (!currentUser) {
      return (
        <div className="dashboard-container">
          <div className="ss-selection-page">
            <div className="ss-page-center">
              <div className="ss-page-error-icon">🔒</div>
              <h2 className="ss-page-error-title">Sign In Required</h2>
              <p className="ss-page-error-msg">
                You need to be signed in to build your stable.
              </p>
              <BackNav />
            </div>
          </div>
        </div>
      );
    }
  
    if (resolvedAccess === ACCESS.NOT_FOUND) {
      return (
        <div className="dashboard-container">
          <div className="ss-selection-page">
            <div className="ss-page-center">
              <div className="ss-page-error-icon">🏇</div>
              <h2 className="ss-page-error-title">Game Not Found</h2>
              <p className="ss-page-error-msg">
                This game doesn't exist or you don't have access to it.
              </p>
              <BackNav />
            </div>
          </div>
        </div>
      );
    }
  
    if (resolvedAccess === ACCESS.ERROR) {
      return (
        <div className="dashboard-container">
          <div className="ss-selection-page">
            <ErrorScreen message={error} onRetry={retry} />
          </div>
        </div>
      );
    }
  
    if (resolvedAccess === ACCESS.FINALISED) {
      return (
        <div className="dashboard-container">
          <div className="ss-selection-page">
            <FinalisedScreen slate={slate} slateId={slateId} />
          </div>
        </div>
      );
    }

    if (resolvedAccess === ACCESS.PRICES_NOT_LOCKED || resolvedAccess === ACCESS.ENTRIES_CLOSED) {
      return (
        <div className="dashboard-container">
          <div className="ss-selection-page">
            <EntriesClosedScreen slate={slate} />
          </div>
        </div>
      );
    }
  
    // ── Main game view ────────────────────────────────────────────────────────
    return (
      <EntryProvider
        slateMetadata={slateMetadata}
        racesMetadata={racesMetadata}
        initialEntryData={initialEntryData}
      >
        <div className="dashboard-container">
        <div className="ss-selection-page">

          {/* Optional: page-level header with game name / logo */}
          {slate?.title && (
            <header className="ss-page-game-header">
              <span className="ss-page-game-title">{slate.title}</span>
              {slate.meetingDate && (
                <span className="ss-page-game-date">
                  {new Date(slate.meetingDate).toLocaleDateString('en-AU', {
                    weekday: 'short',
                    day:     'numeric',
                    month:   'short',
                  })}
                </span>
              )}
            </header>
          )}

          {/* Deep-link reallocation banner */}
          {reallocBanner && (
            <div style={{
              display:      'flex',
              alignItems:   'center',
              gap:          10,
              padding:      '12px 20px',
              margin:       '0 0 16px',
              borderRadius: 8,
              fontSize:     13,
              fontWeight:   600,
              lineHeight:   1.5,
              background:
                reallocBanner.status === 'success' ? '#f0fdf4' :
                reallocBanner.status === 'error'   ? '#fef2f2' :
                reallocBanner.status === 'manual'  ? '#FFF8E1' :
                '#f1f5f9',
              border:
                reallocBanner.status === 'success' ? '1px solid #bbf7d0' :
                reallocBanner.status === 'error'   ? '1px solid #fecaca' :
                reallocBanner.status === 'manual'  ? '1px solid #FDE68A' :
                '1px solid #e2e8f0',
              color:
                reallocBanner.status === 'success' ? '#15803d' :
                reallocBanner.status === 'error'   ? '#dc2626' :
                reallocBanner.status === 'manual'  ? '#92400e' :
                '#334155',
            }}>
              <span style={{ flex: 1 }}>
                {reallocBanner.status === 'processing' && '⟳ Applying your selection…'}
                {reallocBanner.status === 'success'    && `✓ Done — ${reallocBanner.note}`}
                {reallocBanner.status === 'error'      && `⚠ ${reallocBanner.note}`}
                {reallocBanner.status === 'manual'     && `💰 ${reallocBanner.note}`}
              </span>
              <button
                type="button"
                onClick={() => setReallocBanner(null)}
                style={{
                  background: 'none', border: 'none',
                  cursor: 'pointer', fontSize: 16,
                  color: 'inherit', opacity: 0.6, flexShrink: 0,
                }}
                aria-label="Dismiss"
              >
                ✕
              </button>
            </div>
          )}

          {/* In-game reallocation result banner */}
          {(reallocResult || reallocError) && (
            <div style={{
              display:      'flex',
              alignItems:   'center',
              gap:          10,
              padding:      '12px 20px',
              margin:       '0 0 12px',
              borderRadius: 8,
              fontSize:     13,
              fontWeight:   600,
              background:   reallocResult ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
              border:       reallocResult ? '1px solid #22c55e'    : '1px solid #ef4444',
              color:        reallocResult ? '#4ade80'              : '#f87171',
            }}>
              <span style={{ flex: 1 }}>
                {reallocResult ? `✓ ${reallocResult}` : `⚠ ${reallocError}`}
              </span>
              <button
                type="button"
                onClick={() => { setReallocResult(null); setReallocError(null); }}
                style={{
                  background: 'none', border: 'none',
                  cursor: 'pointer', fontSize: 16,
                  color: 'inherit', opacity: 0.6, flexShrink: 0,
                }}
                aria-label="Dismiss"
              >
                ✕
              </button>
            </div>
          )}

          {/* Locked-stable banner — shown when player has already submitted */}
          {existingEntry?.status === 'submitted' && (
            <div style={{
              display:      'flex',
              alignItems:   'center',
              gap:          10,
              padding:      '12px 18px',
              margin:       '0 0 14px',
              borderRadius: 8,
              background:   perRaceTradeWindow
                ? 'rgba(34,197,94,0.10)'
                : 'rgba(99,102,241,0.10)',
              border:       perRaceTradeWindow
                ? '1px solid rgba(34,197,94,0.35)'
                : '1px solid rgba(99,102,241,0.35)',
              color:        perRaceTradeWindow ? '#4ade80' : '#A5B4FC',
              fontSize:     13,
              fontWeight:   600,
              lineHeight:   1.4,
            }}>
              {perRaceTradeWindow
                ? '🔄 Game in progress — you can buy or sell equity on races that haven\'t jumped yet.'
                : '🔒 Your stable is locked in — no further changes can be made.'
              }
            </div>
          )}

          {/* Main selection workspace */}
          <SelectionPanel
            slateData={slate}
            racesData={races}
            initialEntryData={initialEntryData}
            onReview={handleOpenReview}
            slateId={slateId}
            pendingScratchAlerts={liveScratchAlerts}
            onRealloc={handleRealloc}
            onResolveAlert={resolveAlert}
            initialRaceId={targetRaceId}
            scrollRequest={scrollRequest}
            onSell={existingEntry?.status === 'submitted'
              ? (runnerData) => setSellModalRunner(runnerData)
              : undefined
            }
            isEntryLocked={existingEntry?.status === 'submitted'}
          />

          {/* Stable Slip — controlled portal modal (grouped-by-race review + submit) */}
          <ReviewSlip
            isOpen={reviewOpen}
            onClose={handleCloseReview}
            slateData={slate}
            racesData={races}
            onSubmitted={handleSubmitted}
            onNavigateRace={handleNavigateRace}
          />

          {/* Sell equity modal */}
          {sellModalRunner && (
            <SellEquityModal
              runnerId={sellModalRunner.runnerId}
              runnerName={sellModalRunner.runnerName}
              currentEquity={sellModalRunner.currentEquity}
              currentCost={sellModalRunner.currentCost}
              slateId={slateId}
              raceId={sellModalRunner.raceId}
              isRaceLocked={sellModalRunner.isRaceLocked ?? false}
              onClose={() => setSellModalRunner(null)}
              onSold={() => setSellModalRunner(null)}
            />
          )}

        </div>
        </div>
      </EntryProvider>
    );
  };
  
  export default SelectionPage;